import { Button, cn } from "@tellescope/design-system";
import { ArrowLeft, ClipboardPlus } from "lucide-react";
import { BlockProps } from "../../../types";
import { useFormsBlock } from "../../../hooks";
import { BlockHeader } from "../BlockHeader";
import { BlockContent } from "../BlockContent";
import {
  FormsSection,
  FormResponses,
  FormResponse,
  FormResponsePrintablePDF,
  SinglePageFormSubmission,
} from "../../forms-responses";
import { useTranslation } from "react-i18next";
import { BlockEmptyState } from "../BlockEmptyState";

export const FormsBlock = ({
  onMaximize,
  alwaysShowButtons = false,
  className,
  contentClassName,
  ...rest
}: BlockProps) => {
  const { t } = useTranslation("forms");
  const {
    parsedInfo,
    enduser,
    enduserName,
    selectedForm,
    selectedFormResponse,
    openForm,
    openFormResponse,
    closeForm,
    closeFormResponse,
    showNewSubmission,
    openNewSubmission,
    closeNewSubmission,
    isEditingTitle,
    setIsEditingTitle,
    newTitle,
    setNewTitle,
    editMode,
    setEditMode,
    updateInfo,
    duplicateBlock,
    deleteBlock,
    toggleExpanded,
    expanded,
    blockLayout,
    canEditTitle,
    canCreateBlock,
    canDeleteBlock,
    pdfActions,
  } = useFormsBlock({ ...rest, onMaximize, alwaysShowButtons, className });

  return (
    <div className={cn("group", className)}>
      <BlockHeader
        editMode={editMode}
        setEditMode={setEditMode}
        isEditingTitle={isEditingTitle}
        setIsEditingTitle={setIsEditingTitle}
        newTitle={newTitle}
        setNewTitle={setNewTitle}
        canEditTitle={canEditTitle}
        parsedInfo={parsedInfo}
        updateInfo={updateInfo}
        duplicateBlock={duplicateBlock}
        deleteBlock={deleteBlock}
        canCreateBlock={canCreateBlock}
        canDeleteBlock={canDeleteBlock}
        onMaximize={onMaximize}
        alwaysShowButtons={alwaysShowButtons}
        actions={
          editMode &&
          !showNewSubmission &&
          !selectedForm &&
          !selectedFormResponse && (
            <Button
              iconOnly
              variant="ghost"
              size="sm"
              color="muted"
              hoverLabel={t("Submit Form")}
              onClick={openNewSubmission}
            >
              <ClipboardPlus />
            </Button>
          )
        }
      />

      <BlockContent
        editMode={editMode}
        setEditMode={setEditMode}
        expanded={expanded}
        layout={blockLayout}
        onToggleExpanded={() => toggleExpanded()}
        className={cn("flex flex-col", contentClassName)}
      >
        {!enduser ? (
          <div className="flex flex-1 items-center justify-center">
            <BlockEmptyState>{t("Loading forms...")}</BlockEmptyState>
          </div>
        ) : showNewSubmission ? (
          <SinglePageFormSubmission
            contactId={enduser.id}
            onSubmit={closeNewSubmission}
            onSaveResponse={closeNewSubmission}
            onCancel={closeNewSubmission}
            className="flex-grow"
          />
        ) : selectedFormResponse ? (
          <>
            <FormResponse
              timestamp={selectedFormResponse.submittedAt || selectedFormResponse.updatedAt}
              response={selectedFormResponse}
              enduserId={enduser.id}
              enduserName={enduserName}
              onBack={closeFormResponse}
              onGeneratePDFClick={() => pdfActions.generateAndDownload()}
              className="flex-grow"
            />
            {/* Off-screen printable content the PDF generator captures on demand — mounted alongside
                the response so the generator's content ref is present whenever Generate PDF is clicked. */}
            <pdfActions.PDFPortal>
              <FormResponsePrintablePDF
                submittedAt={selectedFormResponse.submittedAt || selectedFormResponse.updatedAt}
                response={selectedFormResponse}
                enduserName={enduserName}
                logoDataUrl={pdfActions.logoDataUrl}
              />
            </pdfActions.PDFPortal>
          </>
        ) : selectedForm ? (
          <div className="flex flex-col gap-2">
            <Button variant="ghost" size="sm" className="self-start" onClick={closeForm}>
              <ArrowLeft />
              {selectedForm.formTitle}
            </Button>
            <FormResponses contactId={enduser.id} formId={selectedForm.formId} onResponseSelect={openFormResponse} />
          </div>
        ) : (
          <FormsSection
            contact={enduser}
            onSelectForm={(form) => openForm({ formId: form.formId, formTitle: form.formTitle })}
          />
        )}
      </BlockContent>
    </div>
  );
};
